// Utilitário de construção canônica de agenda (movido de utils/person)
// Flag futura: window.__USE_CANONICAL_PERSON__ para optar pelo uso no componente
// Mantido propositalmente sem dependências de UI para fácil reuso/teste.

import { defineMessages } from 'react-intl';

/**
 * Retorna o ícone baseado exclusivamente no content-type do arquivo.
 * @param {string|object} urlOrFilename
 * @returns {string} classe do ícone FontAwesome
 */
export function getFileIcon(urlOrFilename) {
  if (!urlOrFilename) return 'fa-solid fa-file';

  const MIME_ICON_MAP = {
    // Imagens
    'image/gif': 'fa-solid fa-file-image',
    'image/jpeg': 'fa-solid fa-file-image',
    'image/jpg': 'fa-solid fa-file-image',
    'image/png': 'fa-solid fa-file-image',
    'image/bmp': 'fa-solid fa-file-image',
    'image/svg+xml': 'fa-solid fa-file-image',
    'image/webp': 'fa-solid fa-file-image',

    // Documentos Microsoft / OpenXML
    'application/msword': 'fa-solid fa-file-word',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      'fa-solid fa-file-word',
    'application/vnd.ms-excel': 'fa-solid fa-file-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      'fa-solid fa-file-excel',
    'application/vnd.ms-powerpoint': 'fa-solid fa-file-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      'fa-solid fa-file-powerpoint',

    // OpenDocument / ODF
    'application/vnd.oasis.opendocument.text': 'fa-solid fa-file-word',
    'application/vnd.oasis.opendocument.spreadsheet': 'fa-solid fa-file-excel',
    'application/vnd.oasis.opendocument.presentation':
      'fa-solid fa-file-powerpoint',
    'application/vnd.oasis.opendocument.presentation-template':
      'fa-solid fa-file-powerpoint',

    // Texto / dados
    'text/csv': 'fa-solid fa-file-csv',
    'text/plain': 'fa-solid fa-file-lines',
    'text/html': 'fa-solid fa-file-code',
    'text/css': 'fa-solid fa-file-code',
    'text/javascript': 'fa-solid fa-file-code',
    'application/javascript': 'fa-solid fa-file-code',
    'application/json': 'fa-solid fa-file-code',
    'application/xml': 'fa-solid fa-file-code',
    'text/xml': 'fa-solid fa-file-code',
    'application/pdf': 'fa-solid fa-file-pdf',

    // Arquivos compactados
    'application/zip': 'fa-solid fa-file-zipper',
    'application/x-rar-compressed': 'fa-solid fa-file-zipper',
    'application/x-7z-compressed': 'fa-solid fa-file-zipper',
    'application/x-tar': 'fa-solid fa-file-zipper',
    'application/gzip': 'fa-solid fa-file-zipper',

    // Áudio e vídeo
    'audio/mpeg': 'fa-solid fa-file-audio',
    'audio/wav': 'fa-solid fa-file-audio',
    'video/mp4': 'fa-solid fa-file-video',
    'video/x-msvideo': 'fa-solid fa-file-video',
    'video/quicktime': 'fa-solid fa-file-video',
  };

  // Se for objeto, usa apenas content-type
  if (typeof urlOrFilename === 'object') {
    const { 'content-type': contentType } = urlOrFilename;

    if (contentType && typeof contentType === 'string') {
      const directIcon = MIME_ICON_MAP[contentType.toLowerCase()];
      if (directIcon) return directIcon;
    }

    return 'fa-solid fa-file';
  }

  // Para strings simples, retorna ícone genérico (sem análise de extensão)
  return 'fa-solid fa-file';
}

const messages = defineMessages({
  viewSchedule: {
    id: 'View schedule',
    defaultMessage: 'View schedule',
  },
  downloadExternalSchedule: {
    id: 'View external schedule',
    defaultMessage: 'View external schedule',
  },
  previousSchedule: {
    id: 'Previous schedule',
    defaultMessage: 'Previous schedule',
  },
});

// Retorna { primary, secondary[] }
/**
 * Constrói agenda canônica de uma pessoa.
 * primary: item principal (visita ou download externo)
 * secondary: downloads auxiliares (históricos, etc.)
 * NÃO altera a estrutura previa (backwards compatible).
 * @param {object} person
 * @returns {{ primary: object|undefined, secondary: Array<object> }}
 */
export function buildCanonicalAgenda(person, intl) {
  if (!person) return { primary: undefined, secondary: [] };

  const url = person.url || (person.agenda && person.agenda['@id']); // agenda principal (visita) - prefer `url`, senão `person.agenda['@id']`
  const external = person.pessoa_historico_agenda_externa; // { download }
  const historySchedule = person.pessoa_historico_agenda; // { download }

  let primary;
  const secondary = [];

  if (url) {
    primary = {
      kind: 'primary',
      source: 'url',
      href: url,
      label: intl?.formatMessage
        ? intl.formatMessage(messages.viewSchedule)
        : messages.viewSchedule.defaultMessage,
      action: 'visit',
      icon: 'fa-solid fa-calendar',
    };
    if (external) {
      secondary.push({
        kind: 'secondary',
        source: 'historico_externo',
        href: external.download,
        label: intl?.formatMessage
          ? intl.formatMessage(messages.downloadExternalSchedule)
          : messages.downloadExternalSchedule.defaultMessage,
        action: 'download',
        icon: getFileIcon(external),
      });
    }
  } else if (external) {
    primary = {
      kind: 'primary',
      source: 'historico_externo',
      href: external.download,
      label: intl?.formatMessage
        ? intl.formatMessage(messages.viewSchedule)
        : messages.viewSchedule.defaultMessage,
      action: 'download',
      icon: getFileIcon(external),
    };
  }

  if (historySchedule?.download) {
    secondary.push({
      kind: 'secondary',
      source: 'historico_csv',
      href: historySchedule.download,
      label: intl?.formatMessage
        ? intl.formatMessage(messages.previousSchedule)
        : messages.previousSchedule.defaultMessage,
      action: 'download',
      icon: getFileIcon(historySchedule),
    });
  }

  return { primary, secondary };
}

export function debugAgenda(person) {
  const agenda = buildCanonicalAgenda(person);
  // eslint-disable-next-line no-console
  console.log('[Agenda] canônica', agenda);
  return agenda;
}
